import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { axiosGetCall } from 'src/services/apisCall';

export default function IvrUpdateCard({ selectedIvr, onIvrSelect, userId }) {
  const [ivrList, setIvrList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState('');

  // Debounce timer ref
  const debounceRef = useRef(null);

  // ── Fetch from API ──────────────────────────────────────────────────────────
  const fetchIvrList = useCallback(
    async (search = '') => {
      if (!userId) return; // guard: no user selected
      setLoading(true);
      setError(null);
      try {
        const params = {
          skip: 0,
          limit: 50,
          status: 'A',
          userId,
        };
        if (search?.trim()) params.search = search.trim();

        const data = await axiosGetCall('fetchIvrList', params);
        setIvrList(data?.documents || []);
      } catch (err) {
        setError(err?.message || 'Failed to load IVR list. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Re-fetch (and clear selection) whenever the selected user changes
  useEffect(() => {
    setIvrList([]);
    setInputValue('');
    setError(null);
    onIvrSelect(null); // clear previously selected IVR
    if (userId) {
      fetchIvrList('');
    }
  }, [userId]);

  // Debounced search on input change
  const handleInputChange = (_, value) => {
    setInputValue(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchIvrList(value);
    }, 400);
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 3,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        IVR Selection
      </Typography>

      {/* No user selected — prompt */}
      {!userId && (
        <Alert severity="warning">
          Please select a user first to load IVRs.
        </Alert>
      )}

      {/* API Fetch Error */}
      {userId && error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Tooltip title="Retry">
              <IconButton size="small" onClick={() => fetchIvrList(inputValue)}>
                <Iconify icon="eva:refresh-fill" />
              </IconButton>
            </Tooltip>
          }
        >
          {error}
        </Alert>
      )}

      {/* Searchable Dropdown — only when a user is selected */}
      {userId && (
        <Autocomplete
          fullWidth
          options={ivrList}
          value={selectedIvr}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={(_, newValue) => onIvrSelect(newValue)}
          loading={loading}
          getOptionLabel={(option) =>
            option ? `${option.ivr_title || '—'} (ID: ${option.ivr_id})` : ''
          }
          isOptionEqualToValue={(option, value) => option?.ivr_id === value?.ivr_id}
          filterOptions={(x) => x} // disable client-side filter — server handles it
          noOptionsText={inputValue ? 'No IVRs match your search.' : 'No approved IVRs available.'}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Search & Select IVR"
              placeholder="Type to search by IVR title..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.ivr_id}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{option.ivr_title || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {option.ivr_id}
                  {option.ivr_type ? ` · Type: ${option.ivr_type}` : ''}
                </Typography>
              </Box>
            </li>
          )}
        />
      )}

      {/* Selected IVR chip */}
      {selectedIvr && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Selected: ${selectedIvr.ivr_title} (ID: ${selectedIvr.ivr_id})`}
            color="primary"
            variant="outlined"
            size="small"
            onDelete={() => onIvrSelect(null)}
          />
        </Box>
      )}
    </Box>
  );
}

IvrUpdateCard.propTypes = {
  selectedIvr: PropTypes.object,
  onIvrSelect: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
